<template>
  <ClientOnly v-if="isAbsoluteLink || isMail">
    <a
      :href="link"
      :target="target"
      :class="
        twMerge([
          'default-button-height relative inline-flex cursor-pointer items-center justify-center border text-center transition-colors duration-200 ease-in-out',
          widthClasses,
          baseClasses,
          disabledBaseClasses,
          disabledHoverClasses,
          extraClasses,
          $attrs.class,
        ])
      "
      :data-testid="$attrs['data-testid']"
      v-bind="additionalAttributes"
      :disabled="disabled"
      @click="$emit('click', $event)"
    >
      <div
        v-if="disabled && showSpinner"
        data-testid="button-spinner"
        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
      >
        <IconSpinner class="w-5 animate-spin" :class="spinnerClasses" />
      </div>

      <div
        data-testid="button-text"
        :class="{ invisible: disabled && showSpinner }"
      >
        <slot></slot>
      </div>
    </a>
    <template #fallback>
      <div
        :class="[
          'default-button-height relative inline-flex cursor-pointer items-center justify-center border text-center transition-colors duration-200 ease-in-out',
          widthClasses,
          baseClasses,
          disabledBaseClasses,
          disabledHoverClasses,
          extraClasses,
          $attrs.class,
        ]"
        :data-testid="$attrs['data-testid']"
      >
        <div
          v-if="disabled && showSpinner"
          data-testid="button-spinner"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
        >
          <IconSpinner class="w-5 animate-spin" :class="spinnerClasses" />
        </div>

        <div
          data-testid="button-text"
          :class="{ invisible: disabled && showSpinner }"
        >
          <slot></slot>
        </div>
      </div>
    </template>
  </ClientOnly>
  <component
    :is="element"
    v-else
    :to="link"
    :target="target"
    :class="[
      'default-button-height relative inline-flex cursor-pointer items-center justify-center border text-center transition-colors duration-200 ease-in-out',
      widthClasses,
      baseClasses,
      disabledBaseClasses,
      disabledHoverClasses,
      extraClasses,
      $attrs.class,
    ]"
    :data-testid="$attrs['data-testid']"
    external
    v-bind="additionalAttributes"
    :disabled="disabled"
    @click="$emit('click', $event)"
  >
    <div
      v-if="disabled && showSpinner"
      data-testid="button-spinner"
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
    >
      <IconSpinner class="w-5 animate-spin" :class="spinnerClasses" />
    </div>

    <div
      data-testid="button-text"
      :class="{ invisible: disabled && showSpinner }"
    >
      <slot></slot>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { TButtonVariants } from '@/src/types/common'
import { twMerge } from 'tailwind-merge'
import { VARIANT_CLASSES } from '~/constants/variant-button-classes'

const IconSpinner = defineAsyncComponent(
  () => import('~/assets/icons/Spinner.svg'),
)

defineOptions({
  inheritAttrs: false,
})

const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    link?: string
    disabled?: boolean
    showSpinner?: boolean
    variant?: TButtonVariants
    extraClasses?: string
    target?: string
    type?: string
    skipWidthClasses?: boolean
  }>(),
  {
    variant: 'primary',
    extraClasses: '',
    target: '_self',
    type: 'button',
  },
)

const isMail = computed(() =>
  !props.link ? undefined : props.link.startsWith('mailto'),
)

const isAbsoluteLink = computed(() => {
  if (!props.link) return undefined

  // Ensure consistent rendering between server and client
  // by checking for common external link patterns
  return (
    props.link.startsWith('https') ||
    props.link.startsWith('http') ||
    props.link.includes('://') ||
    props.link.startsWith('//')
  )
})

const additionalAttributes = computed(() => {
  const attrsWithoutType = Object.fromEntries(
    Object.entries(attrs).filter(([key, _]) => key !== 'onClick'),
  )
  if (isAbsoluteLink.value || isMail.value) {
    return { href: props.link, ...attrsWithoutType }
  }

  // Add no-prefetch only to NuxtLink components
  return props.link
    ? { 'no-prefetch': true, ...attrsWithoutType }
    : { type: props.type, ...attrsWithoutType }
})

const widthClasses = computed(() =>
  props.skipWidthClasses ? [] : ['w-fit', 'md:w-auto'],
)

const baseClasses = computed(() => {
  return VARIANT_CLASSES.BASE[props.variant]
})

const disabledBaseClasses = computed(() => {
  return props.showSpinner
    ? VARIANT_CLASSES.DISABLED.WITH_SPINNER[props.variant]
    : VARIANT_CLASSES.DISABLED.WITHOUT_SPINNER[props.variant]
})

const disabledHoverClasses = computed(() => {
  if (props.disabled) {
    if (props.showSpinner) {
      return VARIANT_CLASSES.DISABLED.HOVER[props.variant] || ''
    } else {
      return VARIANT_CLASSES.DISABLED.HOVER_WITHOUT_SPINNER[props.variant] || ''
    }
  }

  return ''
})

const spinnerClasses = computed(() => VARIANT_CLASSES.SPINNER[props.variant])

const element = computed(() => {
  if (isAbsoluteLink.value || isMail.value) {
    return 'a'
  }

  if (props.link) {
    return resolveComponent('NuxtLink')
  }

  return 'button'
})
</script>
